import { ReactNode } from "react";
import { usePromoActionHandler } from "../../../services/hooks/promoMaterial/usePromoActionHandler";
import { usePromoBanners } from "../../../services/hooks/promoMaterial/usePromoBanners";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { selectCustomerLoyaltyOffers } from "../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  PromoBannerCardSpec,
  PromoBannerKind,
  PromoBannerNothing,
  resolvePromoBanner,
} from "../../../utils/promoMaterial/resolvePromoBanner";
import { SliderComponent } from "../../slider/Slider.component";
import LoyaltyFlashOfferBanner from "../LoyaltyFlashOfferBanner/LoyaltyFlashOfferBanner";
import { PromoAnnouncementBanner } from "./PromoAnnouncementBanner";
import { PromoImageBanner } from "./PromoImageBanner";

/**
 * A plain `card.kind !== NOTHING` filter is not a type guard, so the list would
 * stay widened to the whole union and the switch below would need an
 * unreachable `default`. Narrowing here instead makes a fourth banner kind a
 * compile error at the switch — which is the point of having the enum.
 */
const isRenderable = (
  card: PromoBannerCardSpec
): card is Exclude<PromoBannerCardSpec, PromoBannerNothing> =>
  card.kind !== PromoBannerKind.NOTHING;

/**
 * The home carousel, served whole by the loyalty service (story #4087).
 *
 * One strip, in the server's order, mixing the customer's offers with the
 * chain's authored banners — where the app used to render two sliders (offers,
 * then restaurant images) it now renders this. That is why it *replaces* rather
 * than adds: two rails of the same material would compete.
 *
 * [trailing] is for cards the rail does not own — the referral banner, which is
 * an app feature rather than campaign material and so still rides along at the
 * end.
 */
export const PromoBannerRail = ({
  tierSized,
  trailing,
  hidden,
}: {
  tierSized?: boolean;
  trailing?: ReactNode;
  /**
   * Renders nothing, but stays mounted and keeps fetching.
   *
   * The rail belongs to one tab of the home screen, and unmounting it with that
   * tab would throw the fetched banners away: coming back would show no section
   * at all until a fresh round trip landed. The sliders this replaced never
   * flickered because they read offers that were already in Redux.
   */
  hidden?: boolean;
}) => {
  const { banners } = usePromoBanners();
  const offers = useAppSelector(selectCustomerLoyaltyOffers);
  const handleAction = usePromoActionHandler();

  const cards = banners
    .map((banner) => resolvePromoBanner(banner, offers ?? []))
    .filter(isRenderable);

  // After the hooks above, so the fetch keeps its state across tab switches.
  if (hidden || (cards.length === 0 && !trailing)) {
    return null;
  }

  return (
    <SliderComponent>
      {cards.map((card) => {
        switch (card.kind) {
          case PromoBannerKind.OFFER:
            // The offer object owns the card: its icon motif, apply/remove
            // state, expiry and availability are the existing banner's, so a
            // server-fed offer behaves exactly like a locally-built one.
            return (
              <LoyaltyFlashOfferBanner
                key={card.id}
                offer={card.offer}
                tierSized={tierSized}
                copy={
                  card.copy && {
                    title: card.copy.headline,
                    description: card.copy.description,
                  }
                }
              />
            );

          case PromoBannerKind.ANNOUNCEMENT:
            return (
              <PromoAnnouncementBanner
                key={card.id}
                content={card.content}
                action={card.action}
                onAction={handleAction}
                tierSized={tierSized}
              />
            );

          case PromoBannerKind.IMAGE:
            return (
              <PromoImageBanner
                key={card.id}
                content={card.content}
                action={card.action}
                onAction={handleAction}
              />
            );

          default: {
            // Unreachable: `isRenderable` narrowed the union, so `card` is
            // `never` here. A fourth kind makes this line a compile error
            // instead of a card that silently disappears.
            const exhaustive: never = card;

            return exhaustive;
          }
        }
      })}
      {trailing}
    </SliderComponent>
  );
};
